import { ensureAnimationIds } from "../project_migration/AnimationIdMigration";
import { ensureOpenAnswersProperties } from "../project_migration/OpenAnswersMigration";

/**
 * Unified clipboard envelope for timeline and sequence copy/paste.
 * Paste handlers branch on `kind` and context (timeline vs sequence).
 */
export const CLIPBOARD_VERSION = 2;
export const LEGACY_CLIPBOARD_VERSION = 1;

export type ClipboardKind =
  | "interaction"
  | "interactions"
  | "take360"
  | "take3d"
  | "sequenceNodes";

export interface ClipboardLocalizedValue {
  value: string;
  isAutoTranslated: boolean;
}

export interface ClipboardLocalizationSnapshot {
  sourceReferenceLanguage: string;
  sourceCurrentLanguage: string;
  entries: Record<string, Record<string, ClipboardLocalizedValue>>;
}

export type ClipboardContainerLocator =
  | { kind: "take"; takeId: string }
  | { kind: "sequence"; takeId: string; sequenceId: string };

export interface ClipboardSourceContext {
  trainingId: string;
  organizationId: string;
  environmentId: string;
  container?: ClipboardContainerLocator;
}

export type ClipboardDependencyKind =
  | "actor"
  | "prop"
  | "propMetadata"
  | "environmentPosition"
  | "environmentCamera"
  | "takeLocation"
  | "asset"
  | "variable"
  | "sequenceNode"
  | "outfit"
  | "characterPart";

interface ClipboardDependencyBase {
  kind: ClipboardDependencyKind;
  interactionId: string;
  slot: string;
  sourceId: string;
  required: boolean;
}

export interface ClipboardActorDependency extends ClipboardDependencyBase {
  kind: "actor";
  workingName?: string;
  preset?: string;
}

export interface ClipboardPropDependency extends ClipboardDependencyBase {
  kind: "prop";
  propId?: string;
  prefab?: string;
  name?: string;
  environmentMetadataId?: string;
}

export interface ClipboardPropMetadataDependency extends ClipboardDependencyBase {
  kind: "propMetadata";
  parentPropSlot: string;
  metadataType: "tag" | "state" | "inspectDefinition";
}

export interface ClipboardEnvironmentDependency extends ClipboardDependencyBase {
  kind: "environmentPosition" | "environmentCamera" | "takeLocation";
  environmentId?: string;
  displayName?: string;
}

export interface ClipboardAssetDependency extends ClipboardDependencyBase {
  kind: "asset";
  expectedType?: "image" | "video" | "audio" | "pdf" | "unknown";
}

export interface ClipboardVariableDependency extends ClipboardDependencyBase {
  kind: "variable";
  name?: string;
  variableType?: string;
  scope?: string;
  builtIn: boolean;
}

export interface ClipboardSequenceNodeDependency extends ClipboardDependencyBase {
  kind: "sequenceNode";
}

export interface ClipboardOutfitDependency extends ClipboardDependencyBase {
  kind: "outfit";
}

export interface ClipboardCharacterPartDependency extends ClipboardDependencyBase {
  kind: "characterPart";
  partType?: string | number;
}

export type ClipboardDependency =
  | ClipboardActorDependency
  | ClipboardPropDependency
  | ClipboardPropMetadataDependency
  | ClipboardEnvironmentDependency
  | ClipboardAssetDependency
  | ClipboardVariableDependency
  | ClipboardSequenceNodeDependency
  | ClipboardOutfitDependency
  | ClipboardCharacterPartDependency;

interface ClipboardEnvelopeBase {
  kind: ClipboardKind;
  operation?: "copy" | "cut";
  cutId?: string;
  payload: unknown;
}

export interface ClipboardEnvelopeV1 extends ClipboardEnvelopeBase {
  version: 1;
}

export interface ClipboardEnvelopeV2 extends ClipboardEnvelopeBase {
  version: 2;
  localization?: ClipboardLocalizationSnapshot;
  sourceContext?: ClipboardSourceContext;
  dependencies?: ClipboardDependency[];
}

export type ClipboardEnvelope = ClipboardEnvelopeV1 | ClipboardEnvelopeV2;

let inMemoryClipboardEnvelope: ClipboardEnvelope | null = null;
let clipboardPasteTransactionSequence = 0;

export function createClipboardPasteTransactionId(): string {
  clipboardPasteTransactionSequence += 1;
  return `clipboard-paste-${clipboardPasteTransactionSequence}`;
}

// Payload for kind "interactions": an ordered multi-selection of top-level
// Take interactions. The v2 metadata (localization snapshot, dependencies)
// is aggregated across the whole group.
export interface InteractionsPayload {
  interactions: unknown[];
}

export interface SequenceNodesPayload {
  nodes: Array<{
    id: string;
    position: { x: number; y: number };
    data: { interaction: unknown; name: string; color?: string };
  }>;
  commentZones?: Array<{
    id: string;
    position: { x: number; y: number };
    size: { width: number; height: number };
    label: string;
    color?: string;
  }>;
  textboxes?: Array<{
    id: string;
    position: { x: number; y: number };
    size: { width: number; height: number };
    text: string;
    color?: string;
  }>;
  edges: Array<{
    source: string;
    target: string;
    sourceHandle?: string | null;
    targetHandle?: string | null;
  }>;
}

const clipboardKinds = new Set<ClipboardKind>([
  "interaction",
  "interactions",
  "take360",
  "take3d",
  "sequenceNodes",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isClipboardLocalizationSnapshot(
  value: unknown,
): value is ClipboardLocalizationSnapshot {
  if (!isRecord(value)) return false;
  if (typeof value.sourceReferenceLanguage !== "string") return false;
  if (typeof value.sourceCurrentLanguage !== "string") return false;
  if (!isRecord(value.entries)) return false;

  return Object.values(value.entries).every((localizedEntry) => {
    if (!isRecord(localizedEntry)) return false;
    return Object.values(localizedEntry).every(
      (languageValue) =>
        isRecord(languageValue) &&
        typeof languageValue.value === "string" &&
        typeof languageValue.isAutoTranslated === "boolean",
    );
  });
}

function isClipboardSourceContext(
  value: unknown,
): value is ClipboardSourceContext {
  if (!isRecord(value)) return false;
  if (
    typeof value.trainingId !== "string" ||
    typeof value.organizationId !== "string" ||
    typeof value.environmentId !== "string"
  ) {
    return false;
  }
  if (value.container === undefined) return true;
  if (!isRecord(value.container)) return false;
  if (value.container.kind === "take") {
    return typeof value.container.takeId === "string";
  }
  return (
    value.container.kind === "sequence" &&
    typeof value.container.takeId === "string" &&
    typeof value.container.sequenceId === "string"
  );
}

const dependencyKinds = new Set<ClipboardDependencyKind>([
  "actor",
  "prop",
  "propMetadata",
  "environmentPosition",
  "environmentCamera",
  "takeLocation",
  "asset",
  "variable",
  "sequenceNode",
  "outfit",
  "characterPart",
]);

export function isClipboardDependency(
  value: unknown,
): value is ClipboardDependency {
  if (!isRecord(value)) return false;
  if (
    typeof value.kind !== "string" ||
    !dependencyKinds.has(value.kind as ClipboardDependencyKind) ||
    typeof value.interactionId !== "string" ||
    typeof value.slot !== "string" ||
    typeof value.sourceId !== "string" ||
    typeof value.required !== "boolean"
  ) {
    return false;
  }
  if (value.kind === "propMetadata") {
    return (
      typeof value.parentPropSlot === "string" &&
      (value.metadataType === "tag" ||
        value.metadataType === "state" ||
        value.metadataType === "inspectDefinition")
    );
  }
  if (value.kind === "variable") return typeof value.builtIn === "boolean";
  return true;
}

function isClipboardEnvelope(value: unknown): value is ClipboardEnvelope {
  if (!isRecord(value)) return false;
  if (value.version !== 1 && value.version !== 2) return false;
  if (typeof value.kind !== "string") return false;
  if (!clipboardKinds.has(value.kind as ClipboardKind)) return false;
  if (!("payload" in value)) return false;
  if (
    value.operation !== undefined &&
    value.operation !== "copy" &&
    value.operation !== "cut"
  ) {
    return false;
  }
  if (value.cutId !== undefined && typeof value.cutId !== "string") {
    return false;
  }
  if (
    value.version === 2 &&
    value.localization !== undefined &&
    !isClipboardLocalizationSnapshot(value.localization)
  ) {
    return false;
  }
  return true;
}

export function writeClipboard(envelope: ClipboardEnvelope): void {
  inMemoryClipboardEnvelope = envelope;
  void Promise.resolve(
    navigator.clipboard?.writeText(JSON.stringify(envelope)),
  ).catch(() => {
    // The in-memory envelope still supports same-page Creator paste.
  });
}

export function getInMemoryClipboardEnvelope(): ClipboardEnvelope | null {
  return inMemoryClipboardEnvelope;
}

export function readClipboard(text: string): ClipboardEnvelope | null {
  try {
    const parsed = JSON.parse(text) as unknown;
    if (!isClipboardEnvelope(parsed)) return null;
    if (parsed.version !== 2) return parsed;

    // Optional v2 metadata is intentionally fail-open. A malformed metadata
    // block downgrades to the conservative unknown-source paste policy rather
    // than making the interaction payload unusable.
    const sourceContext = isClipboardSourceContext(parsed.sourceContext)
      ? parsed.sourceContext
      : undefined;
    const dependencies =
      Array.isArray(parsed.dependencies) &&
      parsed.dependencies.every(isClipboardDependency)
        ? parsed.dependencies
        : undefined;
    return {
      ...parsed,
      sourceContext,
      dependencies,
    };
  } catch {
    return null;
  }
}

/**
 * Legacy clipboard: raw JSON (interaction or take) without envelope.
 * Returns a v1 envelope because source localization was not captured.
 */
export function readLegacyClipboard(text: string): ClipboardEnvelope | null {
  try {
    const payload = JSON.parse(text) as unknown;
    if (!payload || typeof payload !== "object") return null;
    const obj = payload as Record<string, unknown>;
    if (
      typeof obj.type === "string" &&
      typeof obj.id === "string" &&
      typeof obj.name === "string"
    ) {
      return {
        version: LEGACY_CLIPBOARD_VERSION,
        kind: "interaction",
        payload,
      };
    }
    if (
      typeof obj.interactions === "object" &&
      typeof obj.assetId !== "undefined"
    ) {
      return {
        version: LEGACY_CLIPBOARD_VERSION,
        kind: "take360",
        payload,
      };
    }
    if (typeof obj.actors === "object" && typeof obj.mainCamera === "object") {
      return {
        version: LEGACY_CLIPBOARD_VERSION,
        kind: "take3d",
        payload,
      };
    }
  } catch {
    // Ignore non-Creator clipboard content.
  }
  return null;
}

/**
 * Payloads copied from an older session (or another training) may carry
 * animation references without an `animationId`. Pasted interactions and
 * takes go straight into the store as commands, bypassing `setTakes`, so the
 * lazy animation-id migration runs here on read instead.
 */
function migrateClipboardEnvelope(envelope: ClipboardEnvelope): ClipboardEnvelope {
  const payload = ensureOpenAnswersProperties(ensureAnimationIds(envelope.payload));
  return payload === envelope.payload ? envelope : { ...envelope, payload };
}

export function readClipboardEnvelope(text: string): ClipboardEnvelope | null {
  const envelope = readClipboard(text) ?? readLegacyClipboard(text);
  return envelope ? migrateClipboardEnvelope(envelope) : null;
}

export async function readClipboardEnvelopeFromClipboard(): Promise<ClipboardEnvelope | null> {
  try {
    const text = await navigator.clipboard.readText();
    const envelope = readClipboardEnvelope(text);
    if (envelope) return envelope;
  } catch {
    // Fall through to the same-page Creator clipboard.
  }

  const inMemoryEnvelope = getInMemoryClipboardEnvelope();
  return inMemoryEnvelope ? migrateClipboardEnvelope(inMemoryEnvelope) : null;
}
